import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  useTheme,
  Button,
  TextField,
  MenuItem,
  InputAdornment,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import useDutiesWeeksService from "../../api/services/DutiesWeeksService";
import useTermService from "../../api/services/termService";
import LoadingSpinner from "../../components/LoadingSpinner";
import AssignTeacherDialog from "./AssignTeacherDialog";
import { getUserFromCookies } from "../../utils/Cookie-utils";
import axiosInstance from "../../api/axiosInstance";
import { endpoints } from "../../api/endPoints";

const STORAGE_KEY = "teacherDutyRoster.selectedTermId";

const TeacherDutyRoster = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const {
    fetchDutiesWeeksByTermWithTeachers,
    fetchDutiesWeeksByTerm,
    fetchTeachersByDutiesWeek,
    dutiesWeeks,
    isLoading: dutiesWeeksLoading,
  } = useDutiesWeeksService();

  const { fetchAllActiveTerms, terms } = useTermService();

  // ✅ Remember last selected term (or fallback to ACTIVE/first term)
  const [selectedTermId, setSelectedTermId] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) || "";
  });

  const [dutiesWeeksWithTeachers, setDutiesWeeksWithTeachers] = useState([]);
  const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
  const [selectedDutiesWeek, setSelectedDutiesWeek] = useState(null);

  // ✅ Loading flags to prevent "No duties" flicker
  const [isTermsLoading, setIsTermsLoading] = useState(true);
  const [isWeeksLoading, setIsWeeksLoading] = useState(false);
  const [isTeachersLoading, setIsTeachersLoading] = useState(false);
  const [hasAttemptedLoad, setHasAttemptedLoad] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");

  // ---------------------------
  // Load terms on mount
  // ---------------------------
  useEffect(() => {
    const loadTerms = async () => {
      setIsTermsLoading(true);
      try {
        await fetchAllActiveTerms(0, 100, "ACTIVE");
      } catch (e) {
        console.error("Error loading terms:", e);
      } finally {
        setIsTermsLoading(false);
      }
    };
    loadTerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If service doesn't return a Promise, this ensures we still stop term loading once terms arrive
  useEffect(() => {
    if (terms) setIsTermsLoading(false);
  }, [terms]);

  // ---------------------------
  // Pick default term - prioritize terms with duties weeks
  // ---------------------------
  useEffect(() => {
    if (!terms || terms.length === 0) return;

    const findTermWithDutiesWeeks = async () => {
      // Check stored term first if valid
      const stored = localStorage.getItem(STORAGE_KEY);
      const storedIsValid = stored && terms.some((t) => String(t.id) === String(stored));
      
      if (storedIsValid) {
        // Check if stored term has duties weeks
        try {
          const response = await axiosInstance.get(`${endpoints.dutiesWeeks.getByTerm}${stored}`);
          const weeks = response.data?.data || [];
          if (Array.isArray(weeks) && weeks.length > 0) {
            return stored;
          }
        } catch (error) {
          // If error, continue to check other terms
          console.error(`Error checking duties weeks for stored term ${stored}:`, error);
        }
      }

      // Check all terms in parallel to find one with duties weeks (skip stored term if already checked)
      const termsToCheck = storedIsValid 
        ? terms.filter((t) => String(t.id) !== String(stored))
        : terms;
      
      const checkPromises = termsToCheck.map(async (term) => {
        try {
          const response = await axiosInstance.get(`${endpoints.dutiesWeeks.getByTerm}${term.id}`);
          const weeks = response.data?.data || [];
          return Array.isArray(weeks) && weeks.length > 0 ? term.id : null;
        } catch (error) {
          // Return null if error (term doesn't have duties weeks or error occurred)
          console.error(`Error checking duties weeks for term ${term.id}:`, error);
          return null;
        }
      });

      const results = await Promise.all(checkPromises);
      const termWithDutiesWeeks = results.find((id) => id !== null);
      
      if (termWithDutiesWeeks) {
        return termWithDutiesWeeks;
      }

      // Fallback: if no term has duties weeks, use stored or first active term
      if (storedIsValid) return stored;
      const active = terms.find((t) => t.status === "ACTIVE");
      return active?.id ?? terms[0]?.id;
    };

    const setDefaultTerm = async () => {
      const preferredId = await findTermWithDutiesWeeks();
      if (preferredId && String(preferredId) !== String(selectedTermId)) {
        setSelectedTermId(preferredId);
      }
    };

    setDefaultTerm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [terms]);

  // Persist selection
  useEffect(() => {
    if (selectedTermId) localStorage.setItem(STORAGE_KEY, selectedTermId);
  }, [selectedTermId]);

  // ---------------------------
  // Load duties weeks when term changes
  // ---------------------------
  useEffect(() => {
    if (!selectedTermId) return;

    const loadWeeks = async () => {
      setIsWeeksLoading(true);
      setHasAttemptedLoad(false);
      setDutiesWeeksWithTeachers([]); // reset but UI will be spinner (no flicker)

      try {
        await fetchDutiesWeeksByTermWithTeachers(selectedTermId);
      } catch (error) {
        console.error("Error loading duties weeks:", error);
      } finally {
        setIsWeeksLoading(false);
        setHasAttemptedLoad(true);
      }
    };

    loadWeeks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTermId]);

  // ---------------------------
  // Load teachers for each week
  // ---------------------------
  useEffect(() => {
    const loadTeachersForWeeks = async () => {
      if (!dutiesWeeks) return;

      if (dutiesWeeks.length === 0) {
        setDutiesWeeksWithTeachers([]);
        return;
      }

      setIsTeachersLoading(true);

      try {
        const weeksWithTeachers = await Promise.all(
          dutiesWeeks.map(async (week) => {
            try {
              const teachers = await fetchTeachersByDutiesWeek(week.id);
              return { ...week, teachers: teachers || [] };
            } catch (error) {
              console.error(`Error loading teachers for week ${week.id}:`, error);
              return { ...week, teachers: [] };
            }
          })
        );

        setDutiesWeeksWithTeachers(weeksWithTeachers);
      } finally {
        setIsTeachersLoading(false);
      }
    };

    loadTeachersForWeeks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dutiesWeeks]);

  const handleTermChange = (event) => {
    const newTermId = event.target.value;
    setSelectedTermId(newTermId);
    localStorage.setItem(STORAGE_KEY, newTermId); // immediate persist
  };

  const handleAssignClick = (week) => {
    setSelectedDutiesWeek(week);
    setIsAssignDialogOpen(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return "N/A";
    }
  };

  const getCurrentWeek = () => {
    const now = new Date();
    return dutiesWeeksWithTeachers.find((week) => {
      if (!week.weekStartDate || !week.weekEndDate) return false;
      const start = new Date(week.weekStartDate);
      const end = new Date(week.weekEndDate);
      return now >= start && now <= end;
    });
  };

  const currentWeek = getCurrentWeek();
  const selectedTerm = terms?.find((term) => term.id === selectedTermId);

  // Role permissions
  const user = getUserFromCookies();
  const userRole = user?.data?.role;
  const allowedRoles = ["ADMIN", "MANAGER", "ACADEMIC"];
  const canViewTermSelector = userRole && allowedRoles.includes(userRole);

  // Sort duties weeks
  const sortedDutiesWeeks = useMemo(() => {
    if (!dutiesWeeksWithTeachers || dutiesWeeksWithTeachers.length === 0) return dutiesWeeksWithTeachers;

    return [...dutiesWeeksWithTeachers].sort((a, b) => {
      if (!a.weekStartDate && !b.weekStartDate) return 0;
      if (!a.weekStartDate) return 1;
      if (!b.weekStartDate) return -1;

      const dateA = new Date(a.weekStartDate);
      const dateB = new Date(b.weekStartDate);

      if (dateA.getTime() !== dateB.getTime()) return dateA.getTime() - dateB.getTime();

      if (!a.weekEndDate && !b.weekEndDate) return 0;
      if (!a.weekEndDate) return 1;
      if (!b.weekEndDate) return -1;

      const endA = new Date(a.weekEndDate);
      const endB = new Date(b.weekEndDate);
      return endA.getTime() - endB.getTime();
    });
  }, [dutiesWeeksWithTeachers]);

  // Filter duties weeks by search
  const filteredDutiesWeeks = sortedDutiesWeeks.filter((week) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();

    const weekNameMatch = week.weekName?.toLowerCase().includes(searchLower);

    const startDateFormatted = formatDate(week.weekStartDate).toLowerCase();
    const endDateFormatted = formatDate(week.weekEndDate).toLowerCase();
    const dateMatch = startDateFormatted.includes(searchLower) || endDateFormatted.includes(searchLower);

    const teacherNamesMatch = week.teachers?.some((teacher) =>
      teacher.name?.toLowerCase().includes(searchLower)
    );

    return weekNameMatch || dateMatch || teacherNamesMatch;
  });

  // ✅ One “global loading” condition so you don’t see “no duties” while data is still coming
  const showLoading =
    isTermsLoading ||
    !selectedTermId ||
    isWeeksLoading ||
    dutiesWeeksLoading ||
    isTeachersLoading ||
    !hasAttemptedLoad;

  const showNoWeeks =
    !showLoading && hasAttemptedLoad && Array.isArray(dutiesWeeks) && dutiesWeeks.length === 0;

  return (
    <Box sx={{ m: 2 }}>
      <Header title="TEACHER DUTY ROSTER" />

      {canViewTermSelector && (
        <Box
          sx={{
            mb: 3,
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 3 },
            alignItems: { xs: "stretch", sm: "center" },
            flexWrap: "wrap",
          }}
        >
          <TextField
            select
            label="Select Term"
            value={selectedTermId || ""}
            onChange={handleTermChange}
            variant="filled"
            sx={{
              minWidth: { xs: "100%", sm: 250, md: 300 },
              maxWidth: { xs: "100%", sm: 300 },
            }}
          >
            {terms && terms.length > 0 ? (
              terms.map((term) => (
                <MenuItem key={term.id} value={term.id}>
                  {term.termName}
                </MenuItem>
              ))
            ) : (
              <MenuItem value="" disabled>
                No terms available
              </MenuItem>
            )}
          </TextField>

          {selectedTerm && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                flex: { xs: "1 1 100%", sm: "0 1 auto" },
                minWidth: 0,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  color: colors.grey[100],
                  fontSize: { xs: "0.875rem", sm: "1rem" },
                  wordBreak: "break-word",
                  textAlign: { xs: "center", sm: "left" },
                }}
              >
                Showing duties weeks for: <strong>{selectedTerm.termName}</strong>
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {showLoading ? (
        <LoadingSpinner message="Loading teacher duty roster..." height="300px" />
      ) : showNoWeeks ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <Paper
            elevation={3}
            sx={{
              p: 4,
              textAlign: "center",
              backgroundColor: colors.primary[400],
            }}
          >
            <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
              No Duties Weeks Found
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This term has no duties weeks linked to it. Please create duties weeks first.
            </Typography>
          </Paper>
        </Box>
      ) : (
        <>
          {/* Current Week Supervisor */}
          {currentWeek && (
            <Paper
              elevation={3}
              sx={{
                p: 3,
                mb: 3,
                backgroundColor: colors.primary[400],
                textAlign: "center",
              }}
            >
              <Typography variant="h6" fontWeight="bold">
                This Week is Supervised by:
              </Typography>

              {isTeachersLoading ? (
                <Typography variant="h5" color="text.secondary" fontWeight="bold">
                  Loading assigned teachers...
                </Typography>
              ) : currentWeek.teachers && currentWeek.teachers.length > 0 ? (
                currentWeek.teachers.map((teacher, index) => (
                  <Typography key={teacher.id} variant="h5" color="secondary" fontWeight="bold">
                    {index > 0 && ", "}
                    {teacher.name}
                  </Typography>
                ))
              ) : (
                <Typography variant="h5" color="text.secondary" fontWeight="bold">
                  No teacher assigned
                </Typography>
              )}

              <Typography variant="body1">
                {currentWeek.weekName} ({formatDate(currentWeek.weekStartDate)} -{" "}
                {formatDate(currentWeek.weekEndDate)})
              </Typography>
            </Paper>
          )}

          {/* Full Roster */}
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", color: colors.grey[100] }}>
              Full Term Schedule:
            </Typography>

            <TextField
              placeholder="Search by week name, dates, or teacher name..."
              variant="filled"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{
                minWidth: { xs: "100%", sm: 300, md: 400 },
                maxWidth: { xs: "100%", sm: 400 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          <Paper>
            <List>
              {filteredDutiesWeeks.length > 0 ? (
                filteredDutiesWeeks.map((week) => (
                  <ListItem
                    key={week.id}
                    sx={{
                      borderBottom: "1px solid #ccc",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <ListItemText
                      primary={`${week.weekName} (${formatDate(week.weekStartDate)} - ${formatDate(
                        week.weekEndDate
                      )})`}
                      secondary={
                        isTeachersLoading
                          ? "Loading assigned teachers..."
                          : week.teachers && week.teachers.length > 0
                          ? `Assigned: ${week.teachers.map((t) => t.name).join(", ")}`
                          : "No teacher assigned"
                      }
                    />

                    {canViewTermSelector && (
                      <Button
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => handleAssignClick(week)}
                        sx={{ ml: 2 }}
                      >
                        {week.teachers && week.teachers.length > 0 ? "Change" : "Assign"}
                      </Button>
                    )}
                  </ListItem>
                ))
              ) : searchTerm ? (
                <ListItem>
                  <ListItemText
                    primary="No matching duties weeks found"
                    secondary={`No results match "${searchTerm}". Try a different search term.`}
                  />
                </ListItem>
              ) : (
                <ListItem>
                  <ListItemText primary="No duties weeks found for this term" secondary="Create duties weeks first" />
                </ListItem>
              )}
            </List>
          </Paper>
        </>
      )}

      <AssignTeacherDialog
        open={isAssignDialogOpen}
        onClose={() => {
          setIsAssignDialogOpen(false);
          setSelectedDutiesWeek(null);
        }}
        onSuccess={async () => {
          // refresh weeks for the current selected term
          if (selectedTermId) {
            setIsWeeksLoading(true);
            try {
              await fetchDutiesWeeksByTermWithTeachers(selectedTermId);
            } finally {
              setIsWeeksLoading(false);
            }
          }
        }}
        dutiesWeekId={selectedDutiesWeek?.id}
        dutiesWeekName={selectedDutiesWeek?.weekName}
      />
    </Box>
  );
};

export default TeacherDutyRoster;
